import * as React from 'react';
import styled from 'styled-components';
import { forMidDesktop, forTablet } from '@westwing/ui-kit/breakPoints';
import { caption1, caption2 } from '@westwing/ui-kit/typography';
import template from '@westwing/ui-kit/template';
import SurveySparrow from './surveySparrow';
import { PATHS } from '@ids/router/routes';
import useIDSPush from '@ids/hooks/useIDSPush';
import { useOverwhelmedOverlayDispatch } from '@ids/context/OverwhelmedOverlay';
import { TRACKING_IDS_PAGE_LABELS, useFeatureToggle } from '@ids/appshell';
import { QuestionnairePageProvider, useQuestionnairePageContext } from '@ids/context/QuestionnairePage';
import usePageViewTracking from '@ids/hooks/usePageViewTracking';
import { useQueryParams } from '@ids/hooks';

const StyledQuestionnairePageLayout = styled.div`
    position: relative;
`;

const StyledPrivacyPolicy = styled.div`
    ${caption2}
    color: ${({ theme }) => theme.colors.charcoal100};
    background: ${({ theme }) => theme.colors.white};
    padding: 8px 18px 18px;

    ${forTablet`
        ${caption1}
        margin-top: 0;
        padding: 8px 20% 0px calc(20% + 10px)
    `}

    ${forMidDesktop`
        padding: 8px 24% 0px calc(26% + 10px)
    `}

    a {
        color: ${({ theme }) => theme.colors.charcoal100};
    }
`;

const PrivacyPolicy = () => {
    const questionnairePageData = useQuestionnairePageContext();

    if (!questionnairePageData?.privacyPolicy) {
        return null;
    }

    return (
        <StyledPrivacyPolicy
            data-testid="privacy-policy"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: template(questionnairePageData.privacyPolicy) }}
        />
    );
};

const Questionnaire = React.memo(() => {
    React.useEffect(() => {
        const AppshellContentWrapper = document.body.querySelector(
            '[class^="App__StyledContentWrapper"]'
        ) as HTMLElement;

        if (AppshellContentWrapper) {
            AppshellContentWrapper.style.minHeight = 'auto';
        }

        return () => {
            if (AppshellContentWrapper) {
                AppshellContentWrapper.style.minHeight = '100vh';
            }
        };
    }, []);

    return (
        <StyledQuestionnairePageLayout data-testid="ids-questionnaire-page" id="ids-questionnaire-page">
            <SurveySparrow />
            <PrivacyPolicy />
        </StyledQuestionnairePageLayout>
    );
});

Questionnaire.displayName = 'Questionnaire';

const QuestionnairePage = () => {
    usePageViewTracking(TRACKING_IDS_PAGE_LABELS.QUESTIONNAIRE);
    const queryParams = useQueryParams();
    const { p100IdsOverwhelmedOverlay, p100StudioPriceTiers } = useFeatureToggle();
    const { showOverwhelmedOverlay } = useOverwhelmedOverlayDispatch();
    const push = useIDSPush();

    React.useEffect(() => {
        if (p100StudioPriceTiers && (!queryParams.has('form-id') || !queryParams.has('sku'))) {
            push.pushToSection('PRICE_TIERS');
        }
    }, [p100StudioPriceTiers, push, queryParams]);

    if (p100IdsOverwhelmedOverlay) {
        push(PATHS.LANDING_PAGE);
        showOverwhelmedOverlay();
        return null;
    }

    return (
        <QuestionnairePageProvider>
            <Questionnaire />
        </QuestionnairePageProvider>
    );
};

export default QuestionnairePage;
